import { z } from "zod";

/**
 * RN payouts: each nurse earns a percent of what clients paid for the services, add-ons, and
 * travel on visits they did, plus all of their tips. A visit covered by a membership or package
 * counts at the voucher's value (what was paid for the plan, shared across its vouchers).
 */

export const PAYOUT_SCHEDULES = ["semimonthly", "monthly", "biweekly", "weekly"] as const;
export type PayoutSchedule = (typeof PAYOUT_SCHEDULES)[number];
export const PAYOUT_SCHEDULE_LABELS: Record<PayoutSchedule, string> = {
  semimonthly: "Twice a month (1st–15th, 16th–end)",
  monthly: "Monthly",
  biweekly: "Every two weeks",
  weekly: "Weekly",
};

const isoDate = /^\d{4}-\d{2}-\d{2}$/;
const shift = (date: string, days: number) => new Date(Date.parse(`${date}T12:00:00Z`) + days * 86_400_000).toISOString().slice(0, 10);
const lastOfMonth = (y: number, m: number) => new Date(Date.UTC(y, m, 0)).getUTCDate();

/** The pay period a date falls in. Weekly and every-two-weeks periods count from `anchor` (a period's first day). */
export function payPeriodFor(date: string, schedule: PayoutSchedule, anchor: string): { start: string; end: string } {
  const [y, m, d] = date.split("-").map(Number) as [number, number, number];
  const pad = (n: number) => String(n).padStart(2, "0");
  if (schedule === "semimonthly") {
    return d <= 15 ? { start: `${y}-${pad(m)}-01`, end: `${y}-${pad(m)}-15` } : { start: `${y}-${pad(m)}-16`, end: `${y}-${pad(m)}-${pad(lastOfMonth(y, m))}` };
  }
  if (schedule === "monthly") return { start: `${y}-${pad(m)}-01`, end: `${y}-${pad(m)}-${pad(lastOfMonth(y, m))}` };
  const len = schedule === "weekly" ? 7 : 14;
  const days = Math.round((Date.parse(`${date}T12:00:00Z`) - Date.parse(`${anchor}T12:00:00Z`)) / 86_400_000);
  const start = shift(anchor, Math.floor(days / len) * len);
  return { start, end: shift(start, len - 1) };
}

/** The period before or after one. */
export function adjacentPayPeriod(start: string, end: string, dir: -1 | 1, schedule: PayoutSchedule, anchor: string) {
  return payPeriodFor(dir < 0 ? shift(start, -1) : shift(end, 1), schedule, anchor);
}

export const payoutSettingsSchema = z.object({
  defaultPercent: z.number().min(0).max(100),
  schedule: z.enum(PAYOUT_SCHEDULES),
  anchorDate: z.string().regex(isoDate),
});

export const payoutRateInputSchema = z
  .object({
    staffMemberId: z.string().uuid().nullable().default(null),
    catalogItemId: z.string().uuid().nullable().default(null),
    percent: z.number().min(0).max(100),
  })
  .refine((r) => r.staffMemberId || r.catalogItemId, { message: "Pick a nurse, a service, or both", path: ["staffMemberId"] });
export type PayoutRateInput = z.infer<typeof payoutRateInputSchema>;

export interface PayoutRate {
  id: string;
  staffMemberId: string | null;
  catalogItemId: string | null;
  percent: number;
}

export interface PayoutRates {
  defaultPercent: number;
  rates: PayoutRate[];
  /** Each staff member's overhead percent (taken out before their commission; 100 unless changed). */
  overhead: Record<string, number>;
  /** Each staff member's minimum overhead (% of what the client paid; 0 unless changed). */
  minOverhead: Record<string, number>;
}

export const payoutAdjustmentSchema = z.object({
  staffMemberId: z.string().uuid(),
  periodStart: z.string().regex(isoDate),
  /** + bonus, − correction. */
  amountCents: z
    .number()
    .int()
    .min(-10_000_000)
    .max(10_000_000)
    .refine((n) => n !== 0, "Enter an amount"),
  note: z.string().trim().min(1, "Say what it's for").max(200),
});

export interface PayoutLine {
  billId: string;
  appointmentId: string;
  /** The day the bill was paid. */
  date: string;
  client: string;
  name: string;
  kind: "service" | "add_on" | "travel";
  quantity: number;
  /** Price before discounts. */
  priceCents: number;
  /** What the commission is figured on. */
  baseCents: number;
  percent: number;
  amountCents: number;
  /** Why the base differs from the price ("Covered by Rehydrate Pack", "Member pricing", "Discount"). */
  note: string | null;
  /** An event day's visit: its start time ("2:15 PM"), the nurse on it (for reference), and its tip (on its first line). */
  time?: string | null;
  nurse?: string | null;
  tipCents?: number;
  /** Clinic overhead (the revenue shares on it, times the nurse's overhead percent) taken out before the commission. */
  overheadCents?: number;
  /** An event visit shared between nurses: the event day ("The Noun 10/31") and how many share it. */
  event?: string | null;
  sharedBy?: number;
}

export interface PayoutNurse {
  staffMemberId: string | null;
  /** An event day's own entry ("The Noun 10/31"): its visits count toward no nurse until it's split. */
  eventOccurrenceId?: string | null;
  name: string;
  visits: number;
  baseCents: number;
  commissionCents: number;
  tipsCents: number;
  adjustmentsCents: number;
  totalCents: number;
  /** Clinic overhead taken out before commission, in all. */
  overheadCents?: number;
  lines: PayoutLine[];
  adjustments: { id: string; amountCents: number; note: string; by: string; at: string }[];
  /** Marked paid for this period: these figures are frozen as they were then. */
  paid?: PayoutPaid | null;
}

/** When a payee was marked paid for a period, and by whom. */
export interface PayoutPaid {
  id: string;
  at: string;
  by: string;
}

/** Tips on visits with no nurse are listed (and marked paid) under this id. */
export const NO_NURSE_ID = "00000000-0000-0000-0000-000000000000";

/** Who a payout entry is marked paid under: an event day, a nurse, or "No nurse". */
export const payoutPayeeId = (n: { staffMemberId: string | null; eventOccurrenceId?: string | null }) => n.eventOccurrenceId ?? n.staffMemberId ?? NO_NURSE_ID;

export const revenueShareInputSchema = z.object({
  name: z.string().trim().min(1).max(60),
  percent: z.number().gt(0).max(100),
  contexts: z.array(z.enum(["clinic", "mobile", "event"])).min(1, "Pick where visits count"),
  items: z.enum(["gfe", "all"]),
  includeProducts: z.boolean().default(false),
  includeTravel: z.boolean().default(false),
  active: z.boolean().default(true),
});
export type RevenueShareInput = z.infer<typeof revenueShareInputSchema>;
export type RevenueShare = RevenueShareInput & { id: string };

/** What one revenue share comes to in a period, with the lines it's figured on. */
export interface PayoutShare {
  id: string;
  name: string;
  percent: number;
  baseCents: number;
  amountCents: number;
  lines: { date: string; client: string; name: string; context: "clinic" | "mobile" | "event"; baseCents: number; amountCents: number }[];
  paid?: PayoutPaid | null;
}

/** An event day in a period: who worked when (for pay), and each visit's split. */
export interface PayoutEventDay {
  occurrenceId: string;
  name: string;
  startsAt: string;
  endsAt: string;
  /** Hours were edited (otherwise: the nurses assigned to the event, for its whole time). */
  edited: boolean;
  workers: { staffMemberId: string; name: string; startsAt: string; endsAt: string }[];
  /** byHand: split by hand (otherwise whoever was working when it started; none = nobody, flagged). */
  visits: { appointmentId: string; time: string; client: string; service: string; staffIds: string[]; byHand: boolean }[];
}

/** Either or both: the share of the overhead they carry, and the least overhead on any visit (% of what the client paid). */
export const payoutOverheadSchema = z
  .object({ staffMemberId: z.string().uuid(), percent: z.number().min(0).max(100).optional(), minPercent: z.number().min(0).max(100).optional() })
  .refine((v) => v.percent !== undefined || v.minPercent !== undefined, "Nothing to change.");
export const payoutEventHoursSchema = z.object({
  /** Empty: back to the nurses assigned to the event, for its whole time. */
  workers: z
    .array(z.object({ staffMemberId: z.string().uuid(), startsAt: z.string().datetime({ offset: true }), endsAt: z.string().datetime({ offset: true }) }))
    .max(20)
    .refine((w) => w.every((x) => Date.parse(x.endsAt) > Date.parse(x.startsAt)), "Each nurse's end time must be after their start."),
});
/** Empty: back to the suggested split (whoever was working when it started). */
export const payoutVisitSplitSchema = z.object({ staffIds: z.array(z.string().uuid()).max(10) });

export interface PayoutReport {
  start: string;
  end: string;
  schedule: PayoutSchedule;
  nurses: PayoutNurse[];
  /** Revenue shares (medical director, landlord); empty for a nurse looking at their own. */
  shares: PayoutShare[];
  /** Event days with visits in the period (who worked, each visit's split); empty for a nurse's own view. */
  eventDays: PayoutEventDay[];
  totals: { visits: number; baseCents: number; commissionCents: number; tipsCents: number; adjustmentsCents: number; totalCents: number };
  /** Only the viewer's own figures (no clinic-wide access). */
  ownOnly: boolean;
}

/** One pay period on the Payouts page: who's still owed (the current period: everyone, as a running total). */
export interface UnpaidPeriod {
  start: string;
  end: string;
  /** The period we're in: a running total, not payable until it ends. */
  current: boolean;
  nurses: PayoutNurse[];
  shares: PayoutShare[];
  eventDays: PayoutEventDay[];
  /** Still owed for the period (for the current one, the running total). */
  totalCents: number;
}

export interface UnpaidPayouts {
  schedule: PayoutSchedule;
  ownOnly: boolean;
  current: UnpaidPeriod;
  /** Ended periods with someone still unpaid, newest first. */
  periods: UnpaidPeriod[];
  /** Owed for ended periods. */
  owedCents: number;
}

export const PAYEE_KINDS = ["staff", "share"] as const;
export type PayeeKind = (typeof PAYEE_KINDS)[number];

export const markPayoutPaidSchema = z.object({
  periodStart: z.string().regex(isoDate),
  kind: z.enum(PAYEE_KINDS),
  payeeId: z.string().uuid(),
});
export type MarkPayoutPaid = z.infer<typeof markPayoutPaidSchema>;

/** A payout marked paid (Reports → Payouts, and a nurse's own history). */
export interface PayoutPayment {
  id: string;
  periodStart: string;
  periodEnd: string;
  kind: PayeeKind;
  payeeId: string;
  name: string;
  amountCents: number;
  paidAt: string;
  paidBy: string;
  detail: PayoutNurse | PayoutShare;
}
